import { useClipboard } from '@/hooks/useClipboard.js'
import { buildInviteUrl } from '@/utils/helpers.js'
import { motion, AnimatePresence } from 'framer-motion'
import { Link2, Copy, Check, Share2, ShieldCheck } from 'lucide-react'

export default function InvitePanel({ roomId }) {
  const inviteUrl = buildInviteUrl(roomId)
  const { copied, copy } = useClipboard()

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative overflow-hidden bg-zinc-950 border border-white/5 rounded-2xl p-6 shadow-2xl"
    >
      {/* Visual "Scanning" Line Effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-20">
        <motion.div 
          animate={{ y: [0, 200, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          className="w-full h-[1px] bg-gradient-to-r from-transparent via-orange-500 to-transparent"
        />
      </div>

      <div className="relative z-10">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <Share2 size={16} className="text-orange-500" />
            </div>
            <div>
              <h3 className="text-[10px] font-mono font-black uppercase tracking-[0.2em] text-zinc-500 leading-none mb-1">
                Invitation Link
              </h3>
              <p className="text-xs font-bold text-zinc-200">Awaiting Challenger</p>
            </div>
          </div>
          <ShieldCheck size={18} className="text-zinc-800" />
        </div>

        {/* URL Input Area */}
        <div className="group relative mb-4">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-600/20 to-transparent rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity" />
          
          <div className="relative flex items-center gap-2 bg-black border border-white/10 rounded-xl p-1.5 focus-within:border-orange-500/50 transition-all">
            <div className="pl-3 text-zinc-600">
              <Link2 size={14} />
            </div>
            
            <input
              type="text"
              readOnly
              value={inviteUrl}
              className="flex-1 bg-transparent border-none text-xs font-mono text-orange-200/70 focus:ring-0 cursor-text select-all py-2"
              onFocus={(e) => e.target.select()}
            />

            <button
              onClick={() => copy(inviteUrl)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-[10px] font-black uppercase tracking-tighter transition-all active:scale-95 ${
                copied 
                  ? 'bg-green-500 text-white' 
                  : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300'
              }`}
            >
              <AnimatePresence mode="wait">
                {copied ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="flex items-center gap-2"
                  >
                    <Check size={12} strokeWidth={4} />
                    COPIED
                  </motion.div>
                ) : (
                  <motion.div
                    key="copy"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="flex items-center gap-2"
                  >
                    <Copy size={12} />
                    COPY_URL
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}